// Lab-module copy of doctor-console/HistoriesData/InitialObservation.js's
// LoincAccordionPanelInput — same LOINC panel search + editable results table,
// embedded compactly inside the IPD "Initial Observation" column.
import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import {
  Typography,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
  MenuItem,
  Paper,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  StyledAutocomplete,
  StyledSelect,
  StyledTableCellHistory,
  StyledTableRowHistory,
  StyledTextField,
  theme,
} from "./StyledComponentsLab";
import { ThemeProvider } from "@mui/material/styles";
import axios from "axios";

const unitOptions = ["mg/dL", "g/dL", "mmol/L", "cells/mcL", "µg/L", "L", "%"];

// The results overlay below is a portal that sits well above MUI's default
// modal z-index (1300) — it has to, because it opens on top of the lab report
// modal (z-index 2000/2001). A Select inside it portals its menu to
// document.body at that default 1300, i.e. *underneath* the overlay: the list
// is invisible, but its modal still holds the focus trap, so every field on the
// card stops accepting input until you click away. Lifting the menu above the
// overlay is the fix — same approach as OrderManagementDrawer's
// drawerSelectMenuProps. disableScrollLock keeps the menu from adding body
// padding and shifting the card underneath it.
const OVERLAY_Z_INDEX = 3000;
const overlaySelectMenuProps = {
  disableScrollLock: true,
  sx: { zIndex: OVERLAY_Z_INDEX + 100 },
  PaperProps: { sx: { zIndex: OVERLAY_Z_INDEX + 100, maxHeight: 280 } },
};

function getApiAuthHeaders() {
  const token = sessionStorage.getItem("accessToken") || "";
  return { Authorization: `Bearer ${token}` };
}

/** Title for accordion from database panel or LOINC resolve wrapper */
function getResolvedPanelTitle(resolved, fallback) {
  if (!resolved || typeof resolved !== "object" || Array.isArray(resolved)) {
    return fallback;
  }
  if (resolved.panel_name || resolved.term) {
    return resolved.panel_name || resolved.term;
  }
  const roots = resolved.data ?? resolved.Data;
  if (Array.isArray(roots) && roots.length && roots[0]) {
    const root = roots[0];
    return (
      root.LONG_COMMON_NAME ||
      root.DisplayName ||
      root.displayName ||
      root.COMPONENT ||
      root.LOINC_NUMBER ||
      fallback
    );
  }
  return fallback;
}

const InitialObservationPanel = ({ data, setData }) => {
  const [searchResults, setSearchResults] = useState([]);
  const [selectedPanelOptionId, setSelectedPanelOptionId] = useState(null);
  const [expandedPanelId, setExpandedPanelId] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [overlayOpen, setOverlayOpen] = useState(false);
  const latestSearchTermRef = useRef("");

  // Opened as a fixed, viewport-centered overlay (rather than anchored beside
  // the search box) so its position never depends on where this panel is
  // mounted — a plain table cell or, now, a card inside the scrollable
  // LabReportModal.
  const openOverlay = () => setOverlayOpen(true);
  const panelEntries = Array.isArray(data) ? data : [];

  const extractSearchPanelList = (body) => {
    if (!body) return [];
    if (Array.isArray(body)) return body;
    if (Array.isArray(body.data)) return body.data;
    if (Array.isArray(body.Data)) return body.Data;
    if (Array.isArray(body.results)) return body.results;
    return [];
  };

  // skip top-level test, only include children & grandchildren
  const flattenTests = (items, panelName, seen = new Set()) => {
    let flat = [];
    for (const t of items) {
      if (!t.Children && !t.grandChildren) {
        if (!t.LOINC_NUMBER || seen.has(t.LOINC_NUMBER)) continue;
        seen.add(t.LOINC_NUMBER);

        flat.push({
          id: `${t.LOINC_NUMBER}_${panelName}`,
          test: t.displayName || t.DisplayName || t.Name || "-",
          code: t.LOINC_NUMBER || "-",
          value: "",
          unit: t.Example_UCUM_UNITS || "",
          // Typed by the technician; this catalogue carries no reference range.
          refRange: "",
        });
      }

      if (Array.isArray(t.Children)) {
        flat = flat.concat(flattenTests(t.Children, panelName, seen));
      }
      if (Array.isArray(t.grandChildren)) {
        flat = flat.concat(flattenTests(t.grandChildren, panelName, seen));
      }
    }
    return flat;
  };

  /** Resolved panel API: { tests: [{ test_name, loinc_code, unit_primary, ... }] } */
  const formatResolvedPanelTests = (resolved, panelName) => {
    const tests = resolved?.tests;
    if (!Array.isArray(tests)) return [];
    return tests.map((t, index) => {
      const code = t.loinc_code || t.LOINC_NUMBER || "-";
      const idBase = t.test_id || code || `idx-${index}`;
      const id = `${idBase}_${panelName}_${index}`;
      return {
        id,
        test_id: t.test_id,
        test: t.test_name || t.name || "-",
        code,
        value: "",
        unit: t.unit_primary || t.unit || "",
        // Prefilled when the panel master carries a range, still editable.
        refRange: t.reference_range || t.normal_range || t.ref_range || "",
        group_name: t.group_name || "",
        critical_notes: t.critical_notes || "",
      };
    });
  };

  const buildTestsFromResolveResponse = (resolved, panelName) => {
    if (
      resolved &&
      typeof resolved === "object" &&
      !Array.isArray(resolved) &&
      Array.isArray(resolved.tests)
    ) {
      return formatResolvedPanelTests(resolved, panelName);
    }

    // LOINC resolve: { source: "loinc", loincNumber, data: [ panelRoot, ... ] }
    const loincRoots = resolved?.data ?? resolved?.Data;
    if (
      resolved &&
      typeof resolved === "object" &&
      !Array.isArray(resolved) &&
      Array.isArray(loincRoots) &&
      (resolved.source === "loinc" || resolved.loincNumber != null)
    ) {
      return flattenTests(loincRoots, panelName);
    }

    const items = Array.isArray(resolved)
      ? resolved
      : resolved
        ? [resolved]
        : [];
    return flattenTests(items, panelName);
  };

  // 🔹 fetch panels dynamically from search_test_panels
  const handlePanelSearch = async (value) => {
    const term = typeof value === "string" ? value.trim() : "";
    latestSearchTermRef.current = term;

    if (!term || term.length < 2) {
      setSearchResults([]);
      return;
    }

    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_ROUTE_URL}search_test_panels/`,
        { params: { name: term }, headers: getApiAuthHeaders() }
      );

      if (latestSearchTermRef.current !== term) return;

      const raw = extractSearchPanelList(res.data);
      const results = raw.map((item, index) => ({
        optionId:
          item.id ??
          `${item.source ?? "unknown"}-${item.loincNumber ?? ""}-${item.panel_id ?? ""}-${index}`,
        panel_name: item.term || item.name || "-",
        source: item.source,
        panel_id: item.panel_id,
        loincNumber: item.loincNumber,
      }));

      setSearchResults(results);
    } catch (err) {
      if (latestSearchTermRef.current === term) {
        setSearchResults([]);
      }
      console.error("Error fetching panels:", err);
    }
  };

  // 🔹 debounce search (wait 1s after typing stops)
  useEffect(() => {
    const t = (searchText ?? "").trim();
    if (t.length < 2) {
      latestSearchTermRef.current = t;
      setSearchResults([]);
      return;
    }
    const timeout = setTimeout(() => {
      handlePanelSearch(searchText);
    }, 1000);
    return () => clearTimeout(timeout);
  }, [searchText]);

  const handlePanelSelect = async (panel) => {
    if (!panel) {
      setSelectedPanelOptionId(null);
      return;
    }
    setSelectedPanelOptionId(panel.optionId);

    if (panelEntries.some((p) => p.panelOptionId === panel.optionId)) return;

    let body;
    if (panel.source === "database") {
      if (!panel.panel_id) {
        console.error("database panel missing panel_id", panel);
        return;
      }
      body = { source: "database", panel_id: panel.panel_id };
    } else {
      body = { source: "loinc", loincNumber: panel.loincNumber };
    }

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_ROUTE_URL}resolve_test_panel/`,
        body,
        { headers: getApiAuthHeaders() }
      );

      const resolved = res.data?.data ?? res.data;
      const displayName = getResolvedPanelTitle(resolved, panel.panel_name);

      const formattedTests = buildTestsFromResolveResponse(
        resolved,
        displayName
      );

      const updated = [
        ...panelEntries,
        {
          panelOptionId: panel.optionId,
          panel_name: displayName,
          tests: formattedTests,
        },
      ];

      setData(updated);
      setExpandedPanelId(panel.optionId);
      openOverlay();
    } catch (err) {
      console.error("Error fetching panel tests:", err);
    }
  };

  const handleTestChange = (panelIndex, testId, field, newValue) => {
    const updatedPanels = [...panelEntries];
    updatedPanels[panelIndex].tests = updatedPanels[panelIndex].tests.map(
      (row) => (row.id === testId ? { ...row, [field]: newValue } : row)
    );
    setData(updatedPanels);
  };

  // Panel-level free text — printed on the report as its Interpretation line
  // (see observationCommentText). Kept on the panel entry so it is stored and
  // restored with the rest of the panel.
  const handlePanelCommentChange = (panelIndex, newValue) => {
    const updatedPanels = [...panelEntries];
    updatedPanels[panelIndex] = {
      ...updatedPanels[panelIndex],
      comment: newValue,
    };
    setData(updatedPanels);
  };

  const handleDeleteTest = (panelIndex, testId) => {
    const updatedPanels = [...panelEntries];
    updatedPanels[panelIndex].tests = updatedPanels[panelIndex].tests.filter(
      (row) => row.id !== testId
    );
    setData(updatedPanels);
  };

  const handleDeletePanel = (panelOptionId) => {
    const updated = panelEntries.filter(
      (p) => p.panelOptionId !== panelOptionId
    );
    setData(updated);
    if (expandedPanelId === panelOptionId) setExpandedPanelId(null);
  };

  return (
    <ThemeProvider theme={theme}>
      <div style={{ minWidth: 220, position: "relative" }} onClick={(e) => e.stopPropagation()}>
        <StyledAutocomplete
          size="small"
          fullWidth
          options={searchResults}
          filterOptions={(options) => options}
          getOptionLabel={(opt) => opt?.panel_name || ""}
          isOptionEqualToValue={(a, b) => a?.optionId === b?.optionId}
          value={
            searchResults.find((p) => p.optionId === selectedPanelOptionId) ||
            null
          }
          onInputChange={(e, value, reason) => {
            if (reason === "reset" && value) return;
            setSearchText(value ?? "");
          }}
          onChange={(e, newVal) => {
            setSelectedPanelOptionId(newVal?.optionId ?? null);
            handlePanelSelect(newVal);
          }}
          ListboxProps={{ style: { maxHeight: 280 } }}
          componentsProps={{ popper: { style: { zIndex: 3000 } } }}
          renderInput={(params) => (
            <StyledTextField {...params} placeholder="Search test panel (e.g. CBC)" />
          )}
        />

        {panelEntries.length > 0 && (
          <button
            type="button"
            onClick={() => (overlayOpen ? setOverlayOpen(false) : openOverlay())}
            style={{
              marginTop: 4,
              border: "1px solid #21cdc0",
              background: "#EFFFFE",
              color: "#0E3F66",
              borderRadius: 12,
              padding: "2px 10px",
              fontSize: 11,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            {panelEntries.length} panel{panelEntries.length === 1 ? "" : "s"} added {overlayOpen ? "▲" : "▼"}
          </button>
        )}

        {overlayOpen && panelEntries.length > 0 && ReactDOM.createPortal(
          <>
            <div
              onClick={() => setOverlayOpen(false)}
              onWheel={(e) => e.stopPropagation()}
              style={{ position: "fixed", inset: 0, zIndex: OVERLAY_Z_INDEX - 1, background: "rgba(15,23,42,0.35)" }}
            />
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                position: "fixed",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                zIndex: OVERLAY_Z_INDEX,
                width: 860,
                maxWidth: "90vw",
                maxHeight: "80vh",
                overflowY: "auto",
                background: "#fff",
                border: "1px solid #d0d7de",
                borderRadius: 8,
                boxShadow: "0 12px 32px rgba(0,0,0,0.25)",
                padding: 10,
              }}
            >
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button
                type="button"
                onClick={() => setOverlayOpen(false)}
                style={{ border: "none", background: "none", cursor: "pointer", fontSize: 14, color: "#6B7280", padding: 4 }}
              >
                ✕
              </button>
            </div>
            {panelEntries.map((panel, panelIndex) => (
              <Accordion
                key={panel.panelOptionId || panel.panel_name}
                expanded={expandedPanelId === panel.panelOptionId}
                onChange={() =>
                  setExpandedPanelId((prev) =>
                    prev === panel.panelOptionId ? null : panel.panelOptionId
                  )
                }
                sx={{ mt: 0.5, border: "1px solid #21cdc0", borderRadius: 1 }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                    }}
                  >
                    <Typography variant="subtitle2">{panel.panel_name}</Typography>
                    <Tooltip title="Delete Panel">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeletePanel(panel.panelOptionId);
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </div>
                </AccordionSummary>
                <AccordionDetails>
                  <TableContainer component={Paper} sx={{ border: "1px solid #ccc" }}>
                    <Table size="small">
                      <TableHead>
                        <StyledTableRowHistory>
                          <StyledTableCellHistory>Test Name</StyledTableCellHistory>
                          <StyledTableCellHistory>LOINC Code</StyledTableCellHistory>
                          <StyledTableCellHistory>Group</StyledTableCellHistory>
                          <StyledTableCellHistory>Value</StyledTableCellHistory>
                          <StyledTableCellHistory>Unit</StyledTableCellHistory>
                          <StyledTableCellHistory>Reference Range</StyledTableCellHistory>
                          <StyledTableCellHistory>Actions</StyledTableCellHistory>
                        </StyledTableRowHistory>
                      </TableHead>
                      <TableBody>
                        {panel.tests.map((testRow) => (
                          <StyledTableRowHistory key={testRow.id}>
                            <StyledTableCellHistory>
                              {testRow.critical_notes ? (
                                <Tooltip title={testRow.critical_notes}>
                                  <Typography component="span" variant="body2" sx={{ cursor: "help" }}>
                                    {testRow.test}
                                  </Typography>
                                </Tooltip>
                              ) : (
                                testRow.test
                              )}
                            </StyledTableCellHistory>
                            <StyledTableCellHistory>{testRow.code}</StyledTableCellHistory>
                            <StyledTableCellHistory>{testRow.group_name || "—"}</StyledTableCellHistory>
                            <StyledTableCellHistory>
                              <StyledTextField
                                value={testRow.value}
                                size="small"
                                fullWidth
                                sx={{ minWidth: 130 }}
                                onChange={(e) =>
                                  handleTestChange(panelIndex, testRow.id, "value", e.target.value)
                                }
                              />
                            </StyledTableCellHistory>
                            <StyledTableCellHistory>
                              <StyledSelect
                                size="small"
                                value={testRow.unit}
                                displayEmpty
                                MenuProps={overlaySelectMenuProps}
                                onChange={(e) =>
                                  handleTestChange(panelIndex, testRow.id, "unit", e.target.value)
                                }
                                sx={{
                                  minWidth: 100,
                                  maxWidth: 130,
                                  height: 40,
                                  overflow: "hidden",
                                  whiteSpace: "nowrap",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                <MenuItem value="">
                                  <em>Select Unit</em>
                                </MenuItem>
                                {[
                                  ...new Set(
                                    [testRow.unit, testRow.unit_secondary, ...unitOptions].filter(Boolean)
                                  ),
                                ].map((unit) => (
                                  <MenuItem key={unit} value={unit}>
                                    {unit}
                                  </MenuItem>
                                ))}
                              </StyledSelect>
                            </StyledTableCellHistory>
                            <StyledTableCellHistory>
                              <StyledTextField
                                value={testRow.refRange || ""}
                                size="small"
                                fullWidth
                                sx={{ minWidth: 140 }}
                                placeholder="e.g. 12–16"
                                onChange={(e) =>
                                  handleTestChange(panelIndex, testRow.id, "refRange", e.target.value)
                                }
                              />
                            </StyledTableCellHistory>
                            <StyledTableCellHistory>
                              <Tooltip title="Delete">
                                <IconButton size="small" onClick={() => handleDeleteTest(panelIndex, testRow.id)}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </StyledTableCellHistory>
                          </StyledTableRowHistory>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Typography
                    variant="caption"
                    sx={{ display: "block", mt: 1.5, mb: 0.5, color: "#0E3F66", fontWeight: 600 }}
                  >
                    Comments / Interpretation
                  </Typography>
                  <StyledTextField
                    fullWidth
                    multiline
                    minRows={2}
                    size="small"
                    placeholder="Notes for this panel — printed on the report"
                    value={panel.comment || ""}
                    onChange={(e) =>
                      handlePanelCommentChange(panelIndex, e.target.value)
                    }
                  />
                </AccordionDetails>
              </Accordion>
            ))}
            {/* Values are written straight into `data` as they are typed, so
                Done just closes the card — nothing is submitted here. Sticky so
                it stays reachable however long the panel gets. */}
            <div
              style={{
                position: "sticky",
                bottom: -10,
                display: "flex",
                justifyContent: "flex-end",
                marginTop: 10,
                paddingTop: 8,
                paddingBottom: 10,
                borderTop: "1px solid #E5E7EB",
                background: "#fff",
              }}
            >
              <button
                type="button"
                onClick={() => setOverlayOpen(false)}
                style={{
                  border: "none",
                  // Same blue as the table column headers above.
                  background: "#0E588E",
                  color: "#fff",
                  borderRadius: 8,
                  padding: "8px 24px",
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Done
              </button>
            </div>
            </div>
          </>,
          document.body
        )}
      </div>
    </ThemeProvider>
  );
};

export default InitialObservationPanel;
